import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';

import { SanitizeHtmlPipe } from '@bn/shared/core/pipes/sanitize-html.pipe';
import { BnToastrService, Toast } from '@bn/shared/toastr';

import { BnToastComponent } from '../bn-toast/bn-toast.component';

@Component({
  selector: 'bn-web-ui-toastr-component',
  templateUrl: './bn-toastr.component.html',
  styleUrls: ['./bn-toastr.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BnToastComponent, SanitizeHtmlPipe],
})
export class BnToastrComponent {
  /**
   * How many toasts render in the stack at once — older ones are dropped, not queued.
   * The per-layer offsets in the stylesheet are generated up to this same count.
   */
  public static readonly MAX_VISIBLE_TOASTS = 5;

  private cdr = inject(ChangeDetectorRef);
  private toastrService = inject(BnToastrService);

  public toastsList: Toast[] = [];
  /** Hovering fans the pile out to be read, so nothing counts down until the pointer leaves. */
  public isStackHovered = false;

  public constructor() {
    this.subscribeToToasts();
  }

  public dispose(index: number): void {
    this.toastsList.splice(index, 1);
    this.cdr.detectChanges();
  }

  public setStackHovered(isStackHovered: boolean): void {
    this.isStackHovered = isStackHovered;
    this.cdr.detectChanges();
  }

  public trackByToastId(index: number, item: Toast): string {
    return item.toastId;
  }

  private subscribeToToasts(): void {
    this.toastrService.getToast$().subscribe((toast) => {
      // Repeating what the stack already holds adds nothing — the copy already queued still gets
      // its own turn at the front, so the message is shown either way
      if (this.isAlreadyInStack(toast)) return;

      // Only the newest MAX_VISIBLE_TOASTS render — drop the rest at insert time so a burst of
      // events can't queue stale toasts or grow the list forever. Only the front one counts down.
      this.toastsList = [toast, ...this.toastsList].slice(0, BnToastrComponent.MAX_VISIBLE_TOASTS);
      this.cdr.detectChanges();
    });
  }

  /** Same wording as something already in the stack, regardless of severity or link. */
  private isAlreadyInStack(toast: Toast): boolean {
    return this.toastsList.some((stacked) => stacked.title === toast.title && stacked.message === toast.message);
  }
}
